<template>
  <v-card class="rounded-lg fill-height">
    <v-card-text class="pa-0 fill-height d-flex flex-column">
      <line-chart
        class="line-chart-elevation"
        :height="280"
        :raw-data="removable ? playerStatsMock : playerStats"
        :chart-value-keys="[ { key: selectedVariable + '_DL', type: 'area', label: $t('chart_legends.daily_load') }, { key: selectedVariable + '_MON', type: 'bar', label: 'Monotony'}]"
        x-value="date"
        y-value-prefix="stats."
        :fill="{ type: ['gradient', 'solid'], opacity: [0.25, .5], gradient: { inverseColors: false, shade: 'light', type: 'vertical', opacityFrom: 0.85, opacityTo: 0.55 } }"
        :yaxis="[ { title: { text: $t('chart_legends.load') }, seriesName: selectedVariable + '_DL' }, { title: { text: 'Monotony' }, seriesName: selectedVariable + '_MON', opposite: true }]"
        :annotations="{ xaxis: xAxisAnnotations }"
      >
        <template v-slot:header>
          <v-card-title class="pa-4">
            <v-row
              no-gutters
              align="center"
            >
              <v-col>
                <div
                  class="subtitle-2 grey--text text--grey-100"
                  style="word-break: normal"
                >
                  {{ $t(`variables.${selectedVariable}.long`) }}: {{ $t("card_title.load_overview") }}
                </div>
              </v-col>
              <v-col
                cols="auto"
                class="ml-3"
              >
                <v-btn
                  v-if="removable"
                  icon
                  style="pointer-events: auto"
                  @click="$emit('remove')"
                >
                  <v-icon small>
                    fal fa-times
                  </v-icon>
                </v-btn>
              </v-col>
            </v-row>
          </v-card-title>
        </template>
      </line-chart>

      <v-data-table
        class="px-8 overflow-y-auto"
        :headers="tableHeaders"
        :items="tableRows"
        :loading="loading"
        :items-per-page="-1"
        dense
        hide-default-footer
      />
    </v-card-text>
  </v-card>
</template>

<script>
import LineChart from '@/components/charts/LineChart.vue'
import dayjs from 'dayjs'
import { getDateTimeFormatForUser } from '@/utils/helpers'

export default {
  name: 'LoadOverviewChart',
  components: {
    LineChart
  },
  props: {
    selectedVariable: {
      type: String,
      required: true
    },
    playerStats: {
      type: Array,
      required: true
    },
    date: {
      type: Object,
      default: () => dayjs()
    },
    loading: {
      type: Boolean,
      default: false
    },
    removable: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    xAxisAnnotations() {
      const annotations = []
      const today = this.date.clone()
      const startDate = today.subtract(28, 'day')

      for (let i = 0; i < 29; i = i + 7) {
        const checkDate = startDate.clone().add(i, 'day')
        annotations.push({
          x: checkDate.valueOf()
        })
      }

      return annotations
    },
    tableHeaders() {
      const day0 = this.date.clone().subtract(27, 'day')
      const day7 = day0.clone().add(7, 'day')
      const day14 = day0.clone().add(14, 'day')
      const day21 = day0.clone().add(21, 'day')
      const day28 = day0.clone().add(28, 'day')
      return [
        { text: '', value: 'variable' },
        { text: day0.format(getDateTimeFormatForUser('D. MMM, YYYY')) + ' - ' + day7.subtract(1, 'day').format(getDateTimeFormatForUser('D. MMM, YYYY')), value: 'week_4' },
        { text: day7.format(getDateTimeFormatForUser('D. MMM, YYYY')) + ' - ' + day14.subtract(1, 'day').format(getDateTimeFormatForUser('D. MMM, YYYY')), value: 'week_3' },
        { text: day14.format(getDateTimeFormatForUser('D. MMM, YYYY')) + ' - ' + day21.subtract(1, 'day').format(getDateTimeFormatForUser('D. MMM, YYYY')), value: 'week_2' },
        { text: day21.format(getDateTimeFormatForUser('D. MMM, YYYY')) + ' - ' + day28.subtract(1, 'day').format(getDateTimeFormatForUser('D. MMM, YYYY')), value: 'week_1' }
      ]
    },
    tableRows() {
      const metrics = ['AL', 'STR', 'ACWR', 'FRES']
      const labels = [this.$t('variables.AL.long'), this.$t('variables.STR.long'), this.$t('variables.ACWR.long'), this.$t('variables.FRES.long')]
      const weeks = [1, 2, 3, 4]

      if (this.removable) {
        return metrics.map((metric, i) => {
          const row = { variable: labels[i] }

          weeks.forEach(week => {
            const seed = week * (i + 1)
            let value = Math.round(Math.sin(seed) * 40 + 50)
            if (metric === 'ACWR') value = (value / 50).toFixed(2)
            row[`week_${week}`] = value
          })

          return row
        })
      }

      const indices = [0, 7, 14, 21]

      return metrics.map((metric, i) => {
        const row = { variable: labels[i] }

        weeks.forEach((week, j) => {
          const statKey = `${this.selectedVariable}_${metric}`
          row[`week_${week}`] = this.playerStats[indices[j]]?.stats[statKey] || '-'
        })

        return row
      })
    },
    playerStatsMock() {
      return this.playerStats.map(stat => ({
        date: stat.start_at,
        stats: {
          [this.selectedVariable + '_DL']: Math.round(Math.random() * 100),
          [this.selectedVariable + '_MON']: Math.round(Math.random() * 100)
        }
      }))
    }
  }
}
</script>

<style scoped>
.line-chart-elevation {
  box-shadow: none !important;
}

.line-chart-elevation >>> .v-card__text {
  padding: 0 !important;
}
</style>
